import { useTranslation } from "react-i18next";

import { useYen } from "@/components/MoneyText";

import type { ReportData } from "./report";

function time(value: string | null, locale: string): string {
  if (!value) {
    return "";
  }
  return new Intl.DateTimeFormat(locale === "ja" ? "ja-JP" : locale === "th" ? "th-TH" : "en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(new Date(value));
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={strong ? "flex justify-between font-bold" : "flex justify-between"}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * X report (中間点検) / Z report (日計表) on screen — the same lines as the printed 80 mm slip
 * (design/05 §3.7): sales by way of paying, card fees in total, 消費税, 免税, number of
 * receipts, returns, discounts, 買取 paid out, staff, the drawer. Labels follow the staff
 * member's language (the printed copy is Japanese — the 税理士 reads it).
 */
export function ReportView({ report }: { report: ReportData }) {
  const { t, i18n } = useTranslation();
  const yen = useYen();
  const locale = i18n.resolvedLanguage ?? "ja";
  const minus = (amount: number) => (amount > 0 ? `−${yen(amount)}` : yen(0));
  const { sales, returns, voids, pickups, cash, taxFree } = report;
  return (
    <article
      aria-label={report.kind === "z" ? t("report.title_z") : t("report.title_x")}
      data-testid="report-view"
      className="mx-auto w-full max-w-[26rem] rounded-md border bg-background p-4 font-mono text-[15px] leading-6"
    >
      <h2 className="text-center text-lg font-bold">
        {report.kind === "z" ? t("report.title_z") : t("report.title_x")}
      </h2>
      <p className="text-center">
        {report.registerName} ({report.registerCode})
      </p>
      <hr className="my-2 border-dashed" />
      <dl>
        <Row label={t("report.opened")} value={time(report.openedAt, locale)} />
        {report.closedAt ? (
          <Row label={t("report.closed")} value={time(report.closedAt, locale)} />
        ) : null}
        <Row
          label={t("report.staff_in_charge")}
          value={report.closedByName ?? report.openedByName ?? ""}
        />
      </dl>
      <hr className="my-2 border-dashed" />
      <dl>
        <Row label={t("report.sales_count")} value={t("report.count", { count: sales.count })} />
        <Row label={t("report.gross")} value={yen(sales.gross)} />
        <Row label={t("report.discount")} value={minus(sales.discount)} />
        <Row
          label={t("report.returns")}
          value={`${t("report.count", { count: returns.count })} ${minus(returns.amount)}`}
        />
        {voids.count > 0 ? (
          <Row
            label={t("report.voids")}
            value={`${t("report.count", { count: voids.count })} ${yen(voids.amount)}`}
          />
        ) : null}
        {pickups.count > 0 ? (
          <Row
            label={t("report.pickups")}
            value={`${t("report.count", { count: pickups.count })} ${yen(pickups.amount)}`}
          />
        ) : null}
        <Row label={t("report.net")} value={yen(report.net)} strong />
        <Row label={t("report.consumption_tax")} value={yen(report.consumptionTax)} />
        {taxFree.exemption.count > 0 ? (
          <Row
            label={t("report.tax_free")}
            value={`${t("report.count", { count: taxFree.exemption.count })} ${yen(taxFree.exemption.amount)}`}
          />
        ) : null}
        {taxFree.refundPending.count > 0 ? (
          <>
            <Row
              label={t("report.tax_free_refund")}
              value={yen(taxFree.refundPending.amountIncl)}
            />
            <Row
              label={t("report.tax_free_refund_tax")}
              value={yen(taxFree.refundPending.taxAmount)}
            />
          </>
        ) : null}
        {report.stampTax.receipts > 0 ? (
          <>
            <Row
              label={t("report.stamp_tax")}
              value={`${t("report.count", { count: report.stampTax.receipts })} ${yen(report.stampTax.amount)}`}
            />
            {report.stampTax.affixed < report.stampTax.receipts ? (
              <Row
                label={t("report.stamp_unconfirmed")}
                value={t("report.count", {
                  count: report.stampTax.receipts - report.stampTax.affixed,
                })}
              />
            ) : null}
          </>
        ) : null}
      </dl>
      <hr className="my-2 border-dashed" />
      <h3 className="font-bold">{t("report.by_tender")}</h3>
      <dl>
        {report.tenders.map((tender) => (
          <Row
            key={tender.method}
            label={t(`report.tender.${tender.method}`, {
              defaultValue: tender.labelJa ?? tender.method,
            })}
            value={yen(tender.net)}
          />
        ))}
        {report.fees.length > 0 ? (
          <>
            <Row label={t("report.fee_total")} value={yen(report.feeTotal)} strong />
            {report.fees.map((fee) => (
              <Row
                key={`${fee.method}-${fee.cardBrand ?? ""}-${fee.terminalId ?? ""}`}
                label={`  ${t(`brand.${fee.cardBrand ?? ""}`, {
                  defaultValue: fee.cardBrand ?? fee.method,
                })}`}
                value={yen(fee.fee)}
              />
            ))}
          </>
        ) : null}
      </dl>
      <hr className="my-2 border-dashed" />
      <h3 className="font-bold">{t("report.cash")}</h3>
      <dl>
        <Row label={t("report.opening_float")} value={yen(cash.openingFloat)} />
        <Row label={t("report.cash_sales")} value={yen(cash.cashSales)} />
        <Row label={t("report.cash_refunds")} value={minus(cash.cashRefunds)} />
        <Row label={t("report.pay_in")} value={yen(cash.payIn)} />
        <Row label={t("report.pay_out")} value={minus(cash.payOut)} />
        <Row label={t("report.buylist_payouts")} value={minus(cash.buylistPayouts)} />
        {cash.holdDeposits > 0 ? (
          <Row label={t("report.hold_deposits")} value={yen(cash.holdDeposits)} />
        ) : null}
        <Row label={t("report.expected")} value={yen(cash.expected)} strong />
        {cash.counted !== null ? (
          <>
            <Row label={t("report.counted")} value={yen(cash.counted)} />
            <Row label={t("report.variance")} value={yen(cash.variance ?? 0)} strong />
            {cash.denominations
              .filter((d) => d.count > 0)
              .map((d) => (
                <Row
                  key={d.value}
                  label={`  ${yen(d.value)} × ${String(d.count)}`}
                  value={yen(d.value * d.count)}
                />
              ))}
          </>
        ) : null}
      </dl>
      {report.movements.length > 0 ? (
        <>
          <hr className="my-2 border-dashed" />
          <h3 className="font-bold">{t("report.movements")}</h3>
          <dl>
            {report.movements.map((m, i) => (
              <Row
                key={`${m.type}-${String(i)}`}
                label={`${m.type === "pay_out" ? t("report.pay_out") : t("report.pay_in")} ${m.reason ?? ""}`}
                value={m.type === "pay_out" ? minus(m.amount) : yen(m.amount)}
              />
            ))}
          </dl>
        </>
      ) : null}
      <hr className="my-2 border-dashed" />
      <dl>
        {report.receipts.first ? (
          <Row
            label={t("report.receipts")}
            value={`${report.receipts.first}〜${report.receipts.last ?? ""}`}
          />
        ) : null}
        {!report.receipts.gapless ? (
          <p role="alert" className="font-bold">
            {t("report.receipts_gap")}
          </p>
        ) : null}
        <Row
          label={t("report.buylist")}
          value={t("report.count", { count: report.buylist.count })}
        />
        {report.staff.map((member) => (
          <Row
            key={member.staffId}
            label={`${t("report.staff")} ${member.staffId.slice(0, 8)}`}
            value={`${t("report.count", { count: member.salesCount })} ${yen(member.gross)}`}
          />
        ))}
      </dl>
      <hr className="my-2 border-dashed" />
      <p className="text-center text-sm">{time(report.generatedAt, locale)}</p>
    </article>
  );
}
